import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingPanel } from '@components/common/FloatingPanel';
import type { FloatingPanelMode } from '@components/common/FloatingPanel';
import { useAIAssistantSelector, useAIAssistantActions } from '@domains/aiAssistant';
import AIAssistantChat from './AIAssistantChat';

export interface AIAssistantPanelProps {
  className?: string;
}

export function AIAssistantPanel({ className }: AIAssistantPanelProps ) {
  const isOpen = useAIAssistantSelector( s => s.isPanelOpen );
  const panelMode = useAIAssistantSelector( s => s.panelMode );
  const { togglePanel, setPanelMode } = useAIAssistantActions();
  const { t } = useTranslation( 'interactions' );

  const getInitialWidth = () => {
    if ( typeof window !== 'undefined' ) {
      if ( window.innerWidth < 768 ) {
        return Math.min( 400, window.innerWidth * 0.8 );
      }
      return Math.max( 400, window.innerWidth * 0.25 );
    }
    return 400; // default width
  };

  const [ panelWidth, setPanelWidth ] = useState(() => getInitialWidth());
  const [ initialPosition, setInitialPosition ] = useState({ x: 0, y: 0 });
  const navbarRef = useRef<HTMLElement | null>( null );

  useEffect(() => {
    // In fullscreen the panel's geometry comes from CSS, so computing a
    // floating anchor is pointless — and it would fight the mode transition.
    if ( isOpen && panelMode === 'floating' ) {
      // Get navbar element
      navbarRef.current = document.querySelector( 'header' );
      if ( navbarRef.current ) {
        const navbarRect = navbarRef.current.getBoundingClientRect();
        const newPosition = {
          x: window.innerWidth - panelWidth - 20, // 20px margin from right
          y: navbarRect.height + 10 // 10px margin from navbar
        };
        setInitialPosition( newPosition );
      }
    }
  }, [ isOpen, panelWidth, panelMode ]);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = getInitialWidth();
      setPanelWidth( newWidth );
    };

    window.addEventListener( 'resize', handleResize );
    return () => window.removeEventListener( 'resize', handleResize );
  }, []);

  /**
   * Fullscreen takes over the viewport, so the page underneath must stop
   * scrolling — otherwise the wheel scrolls the dashboard behind the panel and
   * carries the navbar off the top of the screen, leaving a strip of page
   * content above a panel that is anchored to a navbar no longer there.
   *
   * The class also pins the navbar (`global.css`). Locking scroll alone is not
   * enough: if the user had already scrolled down before switching to
   * fullscreen, freezing the page would freeze it with the navbar off-screen.
   */
  useEffect(() => {
    const active = isOpen && panelMode === 'fullscreen';
    document.documentElement.classList.toggle( 'assistant-fullscreen', active );
    return () => document.documentElement.classList.remove( 'assistant-fullscreen' );
  }, [ isOpen, panelMode ]);

  if ( !isOpen ) {
    return null;
  }

  return (
    <FloatingPanel
      width={panelWidth}
      isVisible={isOpen}
      className={className}
      title="Tessy: AI Assistant"
      onClose={togglePanel}
      initialPosition={initialPosition}
      mode={panelMode}
      onModeChange={( mode: FloatingPanelMode ) => setPanelMode( mode )}
      modeLabels={{
        expand: t( 'ai_assistant_expand_fullscreen', 'Pantalla completa' ),
        collapse: t( 'ai_assistant_collapse_floating', 'Ventana flotante' )
      }}
    >
      <AIAssistantChat />
    </FloatingPanel>
  );
};

export default AIAssistantPanel;